/**
 * ChamadoAssignApplyCard — ASSUMIR ou ATRIBUIR o chamado que a conversa está
 * trabalhando (TASK-11421).
 *
 * O Atendente só sugere. Quem clica aplica, e a escrita é dele: o backend usa
 * a mesma regra da triagem (`assign_task_request`, Modelo A) e grava a mesma
 * trilha (`chamados:assign`), com `chamados:assign` no tenant DO CHAMADO.
 *
 * Sem sugestão de pessoa, o botão é "Assumir" (o chamado vai para quem clica).
 * O seletor lista quem o chamado aceita — o mesmo `assignable-users` da
 * triagem, pelo tenant que o servidor leu do chamado ao registrar a proposta.
 * A escolha no seletor vence a sugestão.
 */
import { useState } from 'react'
import { Badge, Button, Card, Group, Select, Text, Tooltip } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconUserCheck } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { formatApiError } from '@/api/errors'
import { useCan } from '@/store/auth'
import { useApplyChamadoAction, useDeclineAction } from '@/hooks/useChat'
import type { ProposedAction } from './ActionProposalCard'

interface Props {
  conversationId: number
  messageId: number
  proposedAction: ProposedAction
}

interface AssignableUsersResponse {
  items: Array<{ id: number; full_name: string | null; email: string }>
}

const STATUS_LABELS: Record<ProposedAction['status'], string> = {
  pending: 'Pendente',
  executed: 'Atribuído',
  failed: 'Falhou',
  declined: 'Recusado',
}
const STATUS_COLORS: Record<ProposedAction['status'], string> = {
  pending: 'yellow',
  executed: 'teal',
  failed: 'red',
  declined: 'gray',
}

// Mesmos códigos que a triagem traduz (`TriagePanel`), mais o gate do chat.
const ERROS: Record<string, string> = {
  assignee_outside_team: 'Essa pessoa não faz parte do time deste chamado.',
  assignee_not_in_tenant: 'Essa pessoa não tem acesso ao ambiente deste chamado.',
  assignee_inactive: 'Essa pessoa está inativa.',
  assignee_not_found: 'Usuário não encontrado.',
  sem_permissao_de_atribuir: 'Você não tem permissão de atribuir chamados neste ambiente.',
}

export function ChamadoAssignApplyCard({ conversationId, messageId, proposedAction }: Props) {
  const { status, params } = proposedAction
  const can = useCan()
  const canAssign = can('chamados', 'assign')

  const applyMutation = useApplyChamadoAction(conversationId)
  const declineMutation = useDeclineAction(conversationId)
  const [escolhido, setEscolhido] = useState<string | null>(null)

  const isPending = status === 'pending'
  const ticketRef = params.protocol_number || (params.task_request_id ? `#${params.task_request_id}` : 'chamado')
  const tenantId = params.tenant_id
  const sugeridoNome = params.assignee_name

  const { data } = useQuery<AssignableUsersResponse>({
    queryKey: ['assignable-users', tenantId, params.task_request_id, false],
    queryFn: async () =>
      (
        await api.get<AssignableUsersResponse>(
          `/v1/tenants/${tenantId}/workflow/${params.task_request_id}/assignable-users`,
        )
      ).data,
    enabled: isPending && canAssign && Boolean(tenantId && params.task_request_id),
    staleTime: 60_000,
  })
  const opcoes = (data?.items ?? []).map((u) => ({
    value: String(u.id),
    label: u.full_name || u.email,
  }))

  const aplicar = (assigneeUserId?: number) =>
    applyMutation.mutate(
      { messageId, assigneeUserId },
      {
        onError: (err: unknown) => {
          const raw = formatApiError(err, 'Falha ao atribuir o chamado.')
          notifications.show({ color: 'red', title: 'Erro', message: ERROS[raw] ?? raw })
        },
      },
    )

  const rotulo = escolhido
    ? `Atribuir a ${opcoes.find((o) => o.value === escolhido)?.label ?? 'selecionado'}`
    : sugeridoNome
      ? `Atribuir a ${sugeridoNome}`
      : null

  return (
    <Card withBorder padding="sm" radius="md" style={{ maxWidth: 480 }}>
      <Group gap="xs" mb="xs" wrap="nowrap">
        <IconUserCheck size={16} />
        <Text size="sm" fw={600} style={{ flex: 1 }}>
          {`Atendimento do ${ticketRef}`}
        </Text>
        <Badge size="xs" color={STATUS_COLORS[status]} variant="light">
          {STATUS_LABELS[status]}
        </Badge>
      </Group>

      {sugeridoNome && (
        <Text size="sm" c="dimmed" mb="xs">
          {`Sugestão do @Atendente: ${sugeridoNome}.`}
        </Text>
      )}

      {isPending && canAssign && opcoes.length > 0 && (
        <Select
          size="xs"
          mb="xs"
          placeholder="Atribuir a outra pessoa…"
          data={opcoes}
          value={escolhido}
          onChange={setEscolhido}
          searchable
          clearable
        />
      )}

      {isPending && (
        <Group gap="xs" mt="xs" wrap="nowrap">
          <Tooltip
            label="Requer permissão de atribuir chamados"
            disabled={canAssign}
            withArrow
            position="top"
          >
            <div>
              <Button
                size="xs"
                color="inovareTeal"
                variant="filled"
                disabled={!canAssign}
                loading={applyMutation.isPending}
                onClick={() => aplicar(escolhido ? Number(escolhido) : undefined)}
              >
                {rotulo ?? 'Assumir'}
              </Button>
            </div>
          </Tooltip>
          <Button
            size="xs"
            color="red"
            variant="light"
            loading={declineMutation.isPending}
            onClick={() => declineMutation.mutate({ messageId })}
          >
            Recusar
          </Button>
        </Group>
      )}
    </Card>
  )
}
